'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useLabsStore, usePack } from '../pack-context';
import { isMetricCorrect } from '../scoring';
import type { LabsDerivedMetric, LabsUnit } from '../types';
import { useInstantMotion } from '../use-instant-motion';
import { PillTag, StepNav } from './_components';
import { StepShell } from './step-shell';

/**
 * M3 · Activity — the practice subject: read its table, compute the derived metrics off it, diagnose
 * it in a sentence. Styled to the SOURCE vanilla prototype (`data-view="m3b"` + `.statement-table` /
 * `.ratio-row-mc` in styles.css), rendered through the Extern finance-sprint design system (fs-*
 * tokens + DM Serif Display / Inter). STATIC for everyone; every number comes from the pack — none is
 * hardcoded in JSX.
 *
 * LAYOUT (from the HTML prototype, NOT the prior Figma cards):
 *   1. A LEFT-aligned header: uppercase eyebrow, the practice subject's serif name, a muted subhead.
 *   2. The subject's table inside a white rounded card with a `.stmt-title` strip on top (subject name
 *      + the amber "practice example" tag). A header row (empty first cell, then the period columns
 *      with the unit, right-aligned, uppercase muted), then the rows in order; SUBTOTAL rows are bold
 *      on a subtle fill; negative values render danger-red. Values are tabular-nums, right-aligned.
 *   3. The metric picker (`.ratio-row-mc`) — one white card holding hairline-separated rows. Each row
 *      is a LABEL BLOCK on the left (serif metric name + period, mono formula, a feedback line) and
 *      the answer CHOICES on the right (pill buttons). Picking grades via `isMetricCorrect` against
 *      the pack's tolerance: correct → green chip + the pack's "correct" line; wrong → light-red chip
 *      + the "wrong" line (and the row stays selectable). Each correct pick records to the store.
 *      Engaging a row (hover/focus-within) boxes the table cells that metric is computed from.
 *   4. Diagnosis (`#m3-reveal`) — a white card: heading, a textarea, then a [char-counter · "Reveal the
 *      answer" text-link] row; the link auto-fills the pack's sample if empty and opens the reveal.
 *
 * THREE FINANCE ASSUMPTIONS LIVED IN THIS SCREEN'S STRUCTURE, and are now data:
 *   • the row LABELS were a hardcoded list in the view, ordered to match a `Record` of values — so the
 *     table could only ever be an income statement. The subject now ships ORDERED, self-describing
 *     rows and this screen just iterates them.
 *   • the subtotal rows were `new Set([2, 4])` — the INDEXES of Gross Profit and Operating Income. A
 *     row now declares its own `emphasis`.
 *   • the cell format was `` `$${v}${magnitude}` ``. It is now the subject's `unit`, sign-first so a
 *     negative reads "-$0.4B" rather than "$-0.4B" (the practice subject has no negatives, but the
 *     red-negative rule is carried faithfully and the real M4 subjects do).
 *
 * Token mapping (SOURCE intent → fs-* token):
 *   - source eyebrow #6B7280                      → fs-text-muted (uppercase, tracked)
 *   - source card #FFFFFF + #E6E4DD               → fs-surface + fs-border, rounded-fs-card
 *   - source `.stmt-title` / table header muted   → fs-text-muted
 *   - source `.subtotal` bold + #FAFAF7 fill      → fs-text (medium) + fs-surface-subtle
 *   - source `.cell-highlight` brand-green pill   → fs-brand-subtle fill + fs-brand text + fs-focus ring (the v2 brand analog)
 *   - source practice tag (accent-soft amber)     → PillTag warning (fs-warning-subtle / fs-warning)
 *   - source selected pill brand-green            → fs-primary fill / fs-on-primary
 *   - source `.ok` green / `.fail` light-red      → fs-success(+subtle) / fs-danger(+subtle)
 *   - source primary CTA brand-green              → BrandButton primary (fs-primary, the v2 brand)
 */

/** The diagnosis box's hard cap (source `#m3-reveal` textarea) — one sentence, not an essay. */
const DIAGNOSIS_MAX_CHARS = 500;

/** LCG period (2^31) — the modulus for the seeded deterministic shuffle RNG below. */
const LCG_MODULUS = 2_147_483_648;

/** A subject cell, e.g. "$55M" / "-$0.4B" — sign first, so the minus never lands inside the unit. */
function formatCell(unit: LabsUnit, value: number): string {
  const sign = value < 0 ? '-' : '';
  return `${sign}${unit.prefix}${Math.abs(value)}${unit.suffix}`;
}

/** The unit shown in a period column header, e.g. `($M)` / `(€B)`. */
function unitLabel(unit: LabsUnit): string {
  return `(${unit.prefix}${unit.suffix})`;
}

/**
 * Deterministic 0..1 RNG (linear-congruential, glibc constants, mod 2^31) used ONLY under automation
 * so the per-visit option shuffle is stable for visual-regression baselines. Pure arithmetic (no
 * bitwise / no assignment-in-expression) — real users get `Math.random` instead.
 */
function makeSeededRng(initialSeed: number): () => number {
  let seed = initialSeed % LCG_MODULUS;
  return () => {
    seed = (seed * 1_103_515_245 + 12_345) % LCG_MODULUS;
    return seed / LCG_MODULUS;
  };
}

/**
 * A per-visit shuffled copy of a metric's options (Fisher–Yates over a seedable RNG), so the correct
 * answer isn't always in the same slot.
 */
function shuffleOptions(options: readonly number[], rng: () => number): number[] {
  const out = [...options];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i] as number;
    out[i] = out[j] as number;
    out[j] = tmp;
  }
  return out;
}

/** Answer-pill state (correct → green / wrong → light red / unpicked → neutral) — avoids a nested ternary. */
function choiceClass(selected: boolean, correct: boolean): string {
  if (selected && correct) {
    return 'border-fs-success bg-fs-success-subtle text-fs-success';
  }
  if (selected) {
    return 'border-fs-danger bg-fs-danger-subtle text-fs-danger';
  }
  return 'border-fs-border text-fs-text hover:border-fs-primary hover:bg-fs-canvas';
}
export function M3Activity() {
  const $ = _c(117);
  const {
    copy,
    m3
  } = usePack();
  const {
    metrics,
    subject,
    tolerance,
    unitSuffix
  } = m3;
  const activityCopy = copy.m3.activity;
  const next = useLabsStore(_temp);
  const setM3Answer = useLabsStore(_temp2);
  const setM3Diagnosis = useLabsStore(_temp3);
  const w = typeof window === "undefined" ? undefined : window as unknown as {
    __labsNoAuto?: boolean;
  };
  const automated = w?.__labsNoAuto || typeof navigator !== "undefined" && navigator.webdriver;
  let t0;
  if ($[0] !== metrics) {
    const rng = automated ? makeSeededRng(1597463007) : Math.random;
    t0 = Object.fromEntries(metrics.map(metric => [metric.key, shuffleOptions(metric.options, rng)]));
    $[0] = metrics;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const shuffled = t0 as Record<string, number[]>;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [picks, setPicks] = useState(t1);
  const [diagnosis, setDiagnosis] = useState("");
  const [revealed, setRevealed] = useState(false);
  const instant = useInstantMotion();
  const [lastPickedKey, setLastPickedKey] = useState(null);
  const [hoverMetricKey, setHoverMetricKey] = useState(null);
  const highlightKey = instant ? metrics[0]?.key : hoverMetricKey ?? lastPickedKey;
  let t2;
  if ($[3] !== highlightKey || $[4] !== metrics) {
    let t3;
    if ($[6] !== highlightKey) {
      t3 = m => m.key === highlightKey;
      $[6] = highlightKey;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const metric_0 = metrics.find(t3);
    t2 = new Set(metric_0?.sources.map(_temp4) ?? []);
    $[3] = highlightKey;
    $[4] = metrics;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const highlightedCells = t2;
  let t3;
  if ($[8] !== setM3Answer) {
    t3 = (metric_1, value) => {
      setPicks(p => ({
        ...p,
        [metric_1.key]: value
      }));
      setLastPickedKey(metric_1.key);
      setM3Answer(metric_1.key, String(value));
    };
    $[8] = setM3Answer;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handlePick = t3;
  let t4;
  if ($[10] !== activityCopy.diagnosisSample || $[11] !== diagnosis) {
    t4 = () => {
      if (!diagnosis.trim()) {
        setDiagnosis(activityCopy.diagnosisSample);
      }
      setRevealed(true);
    };
    $[10] = activityCopy.diagnosisSample;
    $[11] = diagnosis;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleReveal = t4;
  let t5;
  if ($[13] !== activityCopy.eyebrow) {
    t5 = <div className="font-medium text-[11px] text-fs-text-muted uppercase tracking-[0.12em]">{activityCopy.eyebrow}</div>;
    $[13] = activityCopy.eyebrow;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== subject.name) {
    t6 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{subject.name}</h1>;
    $[15] = subject.name;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== activityCopy.subhead) {
    t7 = <p className="text-fs-body-lg text-fs-text-subtle">{activityCopy.subhead}</p>;
    $[17] = activityCopy.subhead;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t8 = <header className="flex flex-col gap-2">{t5}{t6}{t7}</header>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== subject.name) {
    t9 = <span className="font-medium text-fs-body-lg text-fs-text">{subject.name}</span>;
    $[23] = subject.name;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== activityCopy.tableTag) {
    t10 = <PillTag variant="warning">{activityCopy.tableTag}</PillTag>;
    $[25] = activityCopy.tableTag;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = <div className="flex items-center justify-between gap-2 border-fs-border border-b px-4 py-4">{t9}{t10}</div>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="flex-1" />;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== subject.periods || $[32] !== subject.unit) {
    let t14;
    if ($[34] !== subject.unit) {
      t14 = period => <span className="flex-1 text-right" key={period}>{period} {unitLabel(subject.unit)}</span>;
      $[34] = subject.unit;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    t13 = subject.periods.map(t14);
    $[31] = subject.periods;
    $[32] = subject.unit;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[36] !== t13) {
    t14 = <div className="flex items-center px-4 py-3 text-fs-label-md text-fs-text-muted uppercase tracking-[0.06em]">{t12}<div className="flex w-[320px] shrink-0">{t13}</div></div>;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== highlightedCells || $[39] !== instant || $[40] !== subject.periods || $[41] !== subject.rows || $[42] !== subject.unit) {
    let t16;
    if ($[44] !== highlightedCells || $[45] !== instant || $[46] !== subject.periods || $[47] !== subject.unit) {
      t16 = row => {
        const isSubtotal = row.emphasis === "subtotal";
        return <div className={cn("flex items-center border-fs-border border-t px-4 py-3 text-fs-body-md", isSubtotal && "bg-fs-surface-subtle")} key={row.key}><span className={cn("flex-1 text-fs-text", isSubtotal && "font-medium")}>{row.label}</span><div className="flex w-[320px] shrink-0">{row.values.map((v, periodIndex) => {
              const highlighted = highlightedCells.has(`${row.key}:${periodIndex}`);
              const negative = v < 0;
              return <span className="flex flex-1 justify-end" key={`${row.key}:${subject.periods[periodIndex]}`}><span className={cn("rounded-fs-button px-2 py-0.5 text-right tabular-nums", isSubtotal ? "font-medium text-fs-text" : "text-fs-text", negative && "text-fs-danger", !instant && "transition-[color,background-color,box-shadow] duration-200 ease-out", highlighted && "bg-fs-brand-subtle text-fs-brand ring-1 ring-fs-focus")}>{formatCell(subject.unit, v)}</span></span>;
            })}</div></div>;
      };
      $[44] = highlightedCells;
      $[45] = instant;
      $[46] = subject.periods;
      $[47] = subject.unit;
      $[48] = t16;
    } else {
      t16 = $[48];
    }
    t15 = subject.rows.map(t16);
    $[38] = highlightedCells;
    $[39] = instant;
    $[40] = subject.periods;
    $[41] = subject.rows;
    $[42] = subject.unit;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[49] !== t11 || $[50] !== t14 || $[51] !== t15) {
    t16 = <div className="w-full overflow-hidden rounded-fs-card border border-fs-border bg-fs-surface"><div className="overflow-x-auto"><div className="min-w-[460px]">{t11}{t14}{t15}</div></div></div>;
    $[49] = t11;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== activityCopy.metricsHeading) {
    t17 = <h2 className="text-fs-label-md text-fs-text-muted uppercase tracking-[0.06em]">{activityCopy.metricsHeading}</h2>;
    $[53] = activityCopy.metricsHeading;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== activityCopy.metricsHelper) {
    t18 = <p className="text-fs-label-md text-fs-text-muted">{activityCopy.metricsHelper}</p>;
    $[55] = activityCopy.metricsHelper;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== activityCopy.feedbackCorrect || $[58] !== activityCopy.feedbackWrong || $[59] !== handlePick || $[60] !== instant || $[61] !== metrics || $[62] !== picks || $[63] !== shuffled || $[64] !== tolerance || $[65] !== unitSuffix) {
    let t20;
    if ($[67] !== activityCopy.feedbackCorrect || $[68] !== activityCopy.feedbackWrong || $[69] !== handlePick || $[70] !== instant || $[71] !== picks || $[72] !== shuffled || $[73] !== tolerance || $[74] !== unitSuffix) {
      t20 = (metric_2, i) => {
        const pick = picks[metric_2.key];
        const pickIsCorrect = pick !== undefined && isMetricCorrect(metric_2, pick, tolerance);
        return <div className={cn("flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between", i > 0 && "border-fs-border border-t")} key={metric_2.key} onBlur={instant ? undefined : e => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setHoverMetricKey(null);
          }
        }} onFocus={instant ? undefined : () => setHoverMetricKey(metric_2.key)} onMouseEnter={instant ? undefined : () => setHoverMetricKey(metric_2.key)} onMouseLeave={instant ? undefined : () => setHoverMetricKey(null)}><div className="flex min-w-0 flex-col gap-2"><span className="font-instrument text-fs-text text-fs-title-lg leading-tight">{metric_2.name} <span className="whitespace-nowrap">{metric_2.period}</span></span><span className="font-mono text-fs-body-md text-fs-text-subtle tabular-nums">{metric_2.formula}</span>{pick === undefined ? null : <span className={cn("font-medium text-fs-label-md", pickIsCorrect ? "text-fs-success" : "text-fs-danger")}>{pickIsCorrect ? activityCopy.feedbackCorrect : activityCopy.feedbackWrong}</span>}</div><div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">{shuffled[metric_2.key]?.map(opt => {
              const selected = pick === opt;
              const selectedCorrect = selected && isMetricCorrect(metric_2, opt, tolerance);
              return <button aria-pressed={selected} className={cn("min-w-[84px] cursor-pointer rounded-fs-pill border px-4 py-2 font-mono font-semibold text-fs-body-lg tabular-nums transition-colors", choiceClass(selected, selectedCorrect))} key={opt} onClick={() => handlePick(metric_2, opt)} type="button">{`${opt.toFixed(1)}${unitSuffix}`}</button>;
            })}</div></div>;
      };
      $[67] = activityCopy.feedbackCorrect;
      $[68] = activityCopy.feedbackWrong;
      $[69] = handlePick;
      $[70] = instant;
      $[71] = picks;
      $[72] = shuffled;
      $[73] = tolerance;
      $[74] = unitSuffix;
      $[75] = t20;
    } else {
      t20 = $[75];
    }
    t19 = metrics.map(t20);
    $[57] = activityCopy.feedbackCorrect;
    $[58] = activityCopy.feedbackWrong;
    $[59] = handlePick;
    $[60] = instant;
    $[61] = metrics;
    $[62] = picks;
    $[63] = shuffled;
    $[64] = tolerance;
    $[65] = unitSuffix;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[76] !== t19) {
    t20 = <div className="mt-4 flex flex-col">{t19}</div>;
    $[76] = t19;
    $[77] = t20;
  } else {
    t20 = $[77];
  }
  let t21;
  if ($[78] !== t17 || $[79] !== t18 || $[80] !== t20) {
    t21 = <div className="flex flex-col gap-1 rounded-fs-card border border-fs-border bg-fs-surface p-6">{t17}{t18}{t20}</div>;
    $[78] = t17;
    $[79] = t18;
    $[80] = t20;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  let t22;
  let t23;
  if ($[82] !== activityCopy.diagnosisHeading) {
    t22 = <h2 className="text-fs-label-md text-fs-text-muted uppercase tracking-[0.06em]">{activityCopy.diagnosisHeading}</h2>;
    t23 = <label className="sr-only" htmlFor="m3-diagnosis">{activityCopy.diagnosisHeading}</label>;
    $[82] = activityCopy.diagnosisHeading;
    $[83] = t22;
    $[84] = t23;
  } else {
    t22 = $[83];
    t23 = $[84];
  }
  let t24;
  if ($[85] !== setM3Diagnosis) {
    t24 = e_0 => {
      setDiagnosis(e_0.target.value);
      setM3Diagnosis(e_0.target.value);
    };
    $[85] = setM3Diagnosis;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== activityCopy.diagnosisPlaceholder || $[88] !== diagnosis || $[89] !== t24) {
    t25 = <textarea className="w-full resize-y rounded-fs-card border border-fs-border bg-fs-surface px-4 py-3 text-fs-body-lg text-fs-text placeholder:text-fs-text-muted focus:border-fs-focus focus:outline-none focus:ring-1 focus:ring-fs-focus" id="m3-diagnosis" maxLength={DIAGNOSIS_MAX_CHARS} onChange={t24} placeholder={activityCopy.diagnosisPlaceholder} rows={3} value={diagnosis} />;
    $[87] = activityCopy.diagnosisPlaceholder;
    $[88] = diagnosis;
    $[89] = t24;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  let t26;
  if ($[91] !== diagnosis.length) {
    t26 = <span>{diagnosis.length} characters</span>;
    $[91] = diagnosis.length;
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Eye className="h-3.5 w-3.5" strokeWidth={1.75} />;
    $[93] = t27;
  } else {
    t27 = $[93];
  }
  let t28;
  if ($[94] !== handleReveal) {
    t28 = <button className="inline-flex cursor-pointer items-center gap-fs-xxs font-medium text-fs-brand underline-offset-4 transition-colors hover:underline" onClick={handleReveal} type="button">{t27}Reveal the answer</button>;
    $[94] = handleReveal;
    $[95] = t28;
  } else {
    t28 = $[95];
  }
  let t29;
  if ($[96] !== t26 || $[97] !== t28) {
    t29 = <div className="flex items-center justify-between gap-4 text-fs-label-md text-fs-text-muted">{t26}{t28}</div>;
    $[96] = t26;
    $[97] = t28;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  let t30;
  if ($[99] !== activityCopy.revealBody || $[100] !== activityCopy.revealLabel || $[101] !== revealed) {
    t30 = revealed ? <div className="rounded-fs-card border border-fs-brand-subtle bg-fs-brand-subtle p-5 text-fs-body-md text-fs-text-subtle leading-relaxed"><span className="font-medium text-fs-brand">{activityCopy.revealLabel}</span> {activityCopy.revealBody}</div> : null;
    $[99] = activityCopy.revealBody;
    $[100] = activityCopy.revealLabel;
    $[101] = revealed;
    $[102] = t30;
  } else {
    t30 = $[102];
  }
  let t31;
  if ($[103] !== t22 || $[104] !== t23 || $[105] !== t25 || $[106] !== t29 || $[107] !== t30) {
    t31 = <div className="flex flex-col gap-3 rounded-fs-card border border-fs-border bg-fs-surface p-6">{t22}{t23}{t25}{t29}{t30}</div>;
    $[103] = t22;
    $[104] = t23;
    $[105] = t25;
    $[106] = t29;
    $[107] = t30;
    $[108] = t31;
  } else {
    t31 = $[108];
  }
  let t32;
  if ($[109] !== next) {
    t32 = <StepNav onNext={next} />;
    $[109] = next;
    $[110] = t32;
  } else {
    t32 = $[110];
  }
  let t33;
  if ($[111] !== t16 || $[112] !== t21 || $[113] !== t31 || $[114] !== t32 || $[115] !== t8) {
    t33 = <StepShell><div className="flex w-full flex-col gap-8">{t8}{t16}{t21}{t31}{t32}</div></StepShell>;
    $[111] = t16;
    $[112] = t21;
    $[113] = t31;
    $[114] = t32;
    $[115] = t8;
    $[116] = t33;
  } else {
    t33 = $[116];
  }
  return t33;
}
function _temp4(cell) {
  return `${cell.row}:${cell.periodIndex}`;
}
function _temp3(s_1) {
  return s_1.setM3Diagnosis;
}
function _temp2(s_0) {
  return s_0.setM3Answer;
}
function _temp(s) {
  return s.next;
}